import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { baseURL } from "@/utility/api";
import NewCard from "./NewCard";
import { cdnPath } from "@/utility/api";

export default function NewTopProducts() {
  const [price, setPrice] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("courses");
  const [currentMobileCard, setCurrentMobileCard] = useState(0);

  useEffect(() => {
    async function fetchPrices() {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(
          `${baseURL}/product/get-top-product-prices`
        );
        // console.log(response.data);
        setPrice(response.data);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to fetch prices");
      } finally {
        setLoading(false);
      }
    }
    fetchPrices();
  }, []);

  // Reset mobile card index when tab changes
  useEffect(() => {
    setCurrentMobileCard(0);
  }, [activeTab]);

  const categories = ["courses", "products", "cohorts"];

  const data = {
    courses: {
      cards: [
        {
          title: "Mastering Off-Campus Internships",
          heroImage: `${cdnPath}/course101thumbnail.png`,
          duration: "1h 30 min",
          lessons: "7",
          instructor: {
            name: "Charchit Kurra",
            image: `${cdnPath}/charchit_author.jpg`,
          },
          price: {
            current: `₹${price?.coursePrice ?? "249"}`,
            original: `₹${(
              parseInt(price?.coursePrice ?? "249") + 50
            ).toString()}`,
          },
          badges: ["Best Seller", "Popular"],
          isFeatured: true,
          rating: 4.8,
          reviews: 320,
          people_bought: 600,
          link: "/courses/101",
        },
        {
          title: "Mastering Off-Campus Placements",
          heroImage: `${cdnPath}/PlacementCourseV2.png`,
          duration: "2h 15 min",
          lessons: "8",
          instructor: {
            name: "Charchit Kurra",
            image: `${cdnPath}/charchit_author.jpg`,
          },
          price: {
            current: `₹${price?.coursePriceTwo ?? "349"}`,
            original: `₹${(
              parseInt(price?.coursePriceTwo ?? "349") + 150
            ).toString()}`,
          },
          badges: ["New", "Trending"],
          isFeatured: true,
          rating: 4.9,
          reviews: 285,
          people_bought: 450,
          link: "/courses/102",
        },
      ],
    },
    products: {
      cards: [
        {
          title: "Resume Writing Service",
          heroImage: `${cdnPath}/resume102thumbnail.jpg`,
          duration: "2 Days",
          instructor: {
            name: "Charchit Kurra",
            image: `${cdnPath}/charchit_author.jpg`,
          },
          price: {
            current: `₹${price?.productPrice ?? "799"}`,
            original: `₹${(
              parseInt(price?.productPrice ?? "799") + 200
            ).toString()}`,
          },
          badges: ["Trending", "High Demand"],
          isFeatured: true,
          rating: 4.7,
          reviews: 150,
          people_bought: 100,
          link: "/resume-maker",
        },
        {
          title: "Mailing Templates",
          heroImage: `${cdnPath}/	
mailingtemplate.png`,
          duration: "Instant Access",
          instructor: {
            name: "Charchit Kurra",
            image: `${cdnPath}/charchit_author.jpg`,
          },
          price: {
            current: `₹${price?.mailprice ?? "99"}`,
            original: `₹${(
              parseInt(price?.mailprice ?? "99") + 200
            ).toString()}`, // Static as requested
          },
          badges: ["Popular", "Quick Delivery"],
          isFeatured: true,
          rating: 4.6,
          reviews: 95,
          people_bought: 75,
          link: "/bookcall/pay/102?type=product",
        },
      ],
    },
    cohorts: {
      cards: [
        {
          title: "Off-Campus Internship Cohort",
          heroImage: `${cdnPath}/offcampuscohort.jpg`,
          duration: "6 weeks",
          instructor: {
            name: "Charchit Kurra",
            image: `${cdnPath}/charchit_author.jpg`,
          },
          price: {
            current: `₹${price?.cohortPrice ?? "1000"}`,
            original: `₹${(
              parseInt(price?.cohortPrice ?? "1000") + 1000
            ).toString()}`,
          },
          badges: ["Industry Mentor", "Popular"],
          isFeatured: true,
          rating: 4.7,
          reviews: 48,
          people_bought: 50,
          link: "/internship-cohort",
        },
        {
          title: "Off-Campus Placement Cohort",
          heroImage: `${cdnPath}/placementCohort.jpg`,
          duration: "8 weeks",
          instructor: {
            name: "Charchit Kurra",
            image: `${cdnPath}/charchit_author.jpg`,
          },
          price: {
            current: `₹${price?.cohortPriceTwo ?? "9999"}`,
            original: `₹${(
              parseInt(price?.cohortPriceTwo ?? "9999") + 1000
            ).toString()}`,
          },
          badges: ["Intensive", "60 Days"],
          isFeatured: true,
          rating: 4.8,
          reviews: 32,
          people_bought: 35,
          link: "/placement-cohort",
        },
      ],
    },
  };

  const activeData = data[activeTab];

  const handlePrevCard = () => {
    setCurrentMobileCard((prev) =>
      prev === 0 ? activeData.cards.length - 1 : prev - 1
    );
  };

  const handleNextCard = () => {
    setCurrentMobileCard((prev) =>
      prev === activeData.cards.length - 1 ? 0 : prev + 1
    );
  };

  // Loading Component with gradient
  const GradientLoadingComponent = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="relative w-16 h-16 mb-4">
        {/* Spinning ring with gradient */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#202766] via-[#08B1CF] to-[#342B8D] animate-spin">
          <div className="absolute inset-2 rounded-full bg-black"></div>
        </div>
        {/* Pulsing center dot */}
        {/* <div className="absolute inset-6 rounded-full bg-gradient-to-r from-[#202766] via-[#08B1CF] to-[#342B8D] animate-pulse"></div> */}
      </div>
      <div className="text-transparent bg-clip-text bg-gradient-to-r from-[#202766] via-[#08B1CF] to-[#342B8D] font-medium">
        Loading products...
      </div>
    </div>
  );

  return (
    <section
      aria-labelledby="top-products"
      className="relative w-full md:my-20 my-10 py-16 sm:py-20 overflow-hidden"
    >
      {/* Dotted Background Pattern */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "radial-gradient(#404040 1px, transparent 1px)",
          backgroundSize: "20px 20px",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 30%, black 95%)",
        }}
      />

      {/* Top and bottom fade gradients */}
      <div
        className="absolute top-0 left-0 right-0 h-32 z-[1] pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, transparent 100%)",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-24 z-[1] pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, transparent 100%)",
        }}
      />

      {/* Content - positioned relatively to appear above background */}
      <div className="relative z-10 container max-w-6xl mx-auto px-4">
        {/* Heading */}
        <h2
          id="top-products"
          className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-white mb-2"
        >
          Top Products
        </h2>
        <p className="text-center text-gray-400 mb-10">
          Curated for outcomes. Loved by learners.
        </p>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === cat
                  ? "bg-gradient-to-r from-[#149FD4] to-[#4D50E6] text-white shadow-lg"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        {/* Cards Container */}
        {!loading && !error && (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="relative"
          >
            {/* Desktop - Side by side cards */}
            <div className="hidden md:grid md:grid-cols-2 gap-8">
              {activeData.cards.map((card, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex justify-center"
                >
                  <NewCard course={card} />
                </motion.div>
              ))}
            </div>

            {/* Mobile - Swipeable cards */}
            <div className="md:hidden">
              {/* Cards container with swipe */}
              <div className="relative overflow-hidden">
                <div
                  className="flex transition-transform duration-300 ease-in-out"
                  style={{
                    transform: `translateX(-${currentMobileCard * 100}%)`,
                  }}
                >
                  {activeData.cards.map((card, index) => (
                    <div key={index} className="w-full flex-shrink-0 px-2">
                      <div className="flex justify-center">
                        <NewCard course={card} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation arrows */}
              <div className="flex justify-between items-center mt-6 px-4">
                <button
                  onClick={handlePrevCard}
                  className="p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                  aria-label="Previous card"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Dots indicator */}
                <div className="flex gap-2">
                  {activeData.cards.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentMobileCard(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        currentMobileCard === index
                          ? "bg-gradient-to-r from-[#149FD4] to-[#4D50E6] w-6"
                          : "bg-gray-600"
                      }`}
                      aria-label={`Go to card ${index + 1}`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleNextCard}
                  className="p-2 rounded-full bg-gray-800 text-white hover:bg-gray-700 transition-colors"
                  aria-label="Next card"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Loading state - using same spinner for both loading and error */}
        {(loading || error) && <GradientLoadingComponent />}
      </div>
    </section>
  );
}
